import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil, Package, ClipboardList, RotateCcw, MapPin, Upload, Download, FileText, CalendarPlus } from "lucide-react";
import BulkUpload from "@/components/BulkUpload";
import { HireBillingDialog, TRANSPORT, YesNo } from "@/components/equipment/HireBillingDialog";
import { HireChangeHistory } from "@/components/equipment/HireChangeHistory";
import { ExtendHireDialog } from "@/components/equipment/ExtendHireDialog";
import { EquipmentThumb } from "@/components/equipment/EquipmentPhotos";
import { BranchTransferField } from "@/components/equipment/BranchTransferField";
import { EquipmentFormDialog } from "@/components/equipment/EquipmentFormDialog";
import { EquipmentDetailDialog } from "@/components/equipment/EquipmentDetailDialog";
import { StatusBadge, BLOCKING_STATUSES, DUE_FIELDS, fmtDate, isPastDue, localToday } from "@/components/equipment/equipmentMeta";
import { CatalogueFilters, filterCatalogue } from "@/components/equipment/CatalogueFilters";
import { PendingRequestsPanel } from "@/components/equipment/StatusRequests";

const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const EQUIPMENT_CSV_TEMPLATE =
  "asset_id,name,category,location,description,quantity,status,daily_rate,weekly_rate,monthly_rate,replacement_value," +
  "make_model,serial_number,wll,weight_dimensions,asset_owner,accessories,revenue_splits," +
  "cert_valid_date,calibration_valid_date,inspection_due_date,test_due_date,last_inspection_date\n" +
  "JQ-0001,Example Crane,Cranes,,20t mobile crane,3,available,250,1500,5000,180000," +
  "Liebherr LTM 1020,SN12345,20t,24t / 9.5 x 2.5 x 3.5m,,Outriggers + pads,,2027-01-31,2026-12-31,2026-11-30,2027-01-31,2026-05-30\n";
const HIRE_CSV_TEMPLATE =
  "equipment_name,customer,customer_address,quantity,start_date,end_date,rate_type,note,transport,transport_notes,po_received,po_number,invoiced,branch_transfer,transfer_to\n" +
  "Example Crane,ACME Ltd,12 Wharf Rd Lyttelton,1,2026-09-01,2026-09-07,daily,Handle with care,we_deliver_collect,Hiab truck needed,yes,PO-1234,no,no,\n";

const emptyHire = {
  equipment_id: "", customer: "", quantity: 1, start_date: "", end_date: "", rate_type: "daily", note: "",
  customer_address: "", transport: "", transport_notes: "", po_received: false, po_number: "", invoiced: false,
  branch_transfer: false, transfer_to: "",
};

const DUE_SHORT = { inspection_due_date: "Inspection", test_due_date: "Test/Cert", cert_valid_date: "Cert expires", calibration_valid_date: "Calibration" };

const inputCls = "w-full px-3 py-2 border border-zinc-300 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-brand";

export default function Equipment() {
  const { user } = useAuth();
  const canManageEquip = ["admin", "superadmin", "hire_manager"].includes(user.role);
  const canLogHistory = canManageEquip || user.role === "hire_coordinator";
  const canDeleteEquip = ["admin", "superadmin", "hire_manager"].includes(user.role);
  const canDeleteHire = ["admin", "superadmin", "manager", "hire_manager"].includes(user.role);
  const canEditHire = ["admin", "superadmin", "hire_manager", "hire_coordinator"].includes(user.role);
  const [editingHire, setEditingHire] = useState(null);
  const [extendOrder, setExtendOrder] = useState(null);
  const [tab, setTab] = useState("catalogue");
  const [locFilter, setLocFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dueFilter, setDueFilter] = useState("all");
  const [orderFilter, setOrderFilter] = useState("all");
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const d = searchParams.get("due");
    if (d === "soon" || d === "overdue") { setDueFilter(d); setTab("catalogue"); }
    if (searchParams.get("tab") === "orders") { setTab("orders"); setOrderFilter(searchParams.get("invoiced") === "no" ? "not_invoiced" : searchParams.get("view") === "transfers" ? "transfers" : "all"); }
  }, [searchParams]);
  const [equipment, setEquipment] = useState([]);
  const [orders, setOrders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [bulkEquipOpen, setBulkEquipOpen] = useState(false);
  const [bulkHireOpen, setBulkHireOpen] = useState(false);

  // equipment dialog
  const [eqOpen, setEqOpen] = useState(false);
  const [eqEditing, setEqEditing] = useState(null);
  const [eqDeleteId, setEqDeleteId] = useState(null);
  const [detailId, setDetailId] = useState(null);

  // hire dialog
  const [hireOpen, setHireOpen] = useState(false);
  const [hireForm, setHireForm] = useState(emptyHire);
  const [hireSaving, setHireSaving] = useState(false);
  const [hireDeleteId, setHireDeleteId] = useState(null);
  const [billingOrder, setBillingOrder] = useState(null);

  const loadEquipment = useCallback(() => api.get("/equipment").then((r) => setEquipment(r.data)), []);
  const loadOrders = useCallback(() => api.get("/hire-orders").then((r) => setOrders(r.data)).catch(() => {}), []);
  const [requests, setRequests] = useState([]);
  const loadRequests = useCallback(() => api.get("/equipment/status-requests").then((r) => setRequests(r.data)).catch(() => {}), []);
  const loadLocations = useCallback(() => api.get("/locations").then((r) => setLocations(r.data)).catch(() => {}), []);

  useEffect(() => {
    loadEquipment();
    loadOrders();
    loadLocations();
    loadRequests();
  }, [loadEquipment, loadOrders, loadLocations, loadRequests]);

  const openNewEquip = () => { setEqEditing(null); setEqOpen(true); };
  const openEditEquip = (e) => { setEqEditing(e); setDetailId(null); setEqOpen(true); };
  const detailEquip = equipment.find((e) => e.id === detailId) || null;
  const onDetailChange = (updated) => setEquipment((list) => list.map((x) => (x.id === updated.id ? updated : x)));

  const doDeleteEquip = async () => {
    try {
      await api.delete(`/equipment/${eqDeleteId}`);
      toast.success("Equipment deleted");
      setEqDeleteId(null);
      loadEquipment();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setEqDeleteId(null);
    }
  };

  const openNewHire = () => { setEditingHire(null); setHireForm(emptyHire); setHireOpen(true); };
  const openEditHire = (o) => {
    setEditingHire(o);
    setHireForm({ ...emptyHire, ...Object.fromEntries(Object.keys(emptyHire).map((k) => [k, o[k] ?? emptyHire[k]])) });
    setHireOpen(true);
  };
  const selectedEquip = equipment.find((e) => e.id === hireForm.equipment_id);
  const atSite = locFilter === "all" ? equipment : equipment.filter((e) => (e.location || "") === locFilter);
  const catalogue = filterCatalogue(atSite, statusFilter, dueFilter);

  const exportRegister = async () => {
    try {
      const res = await api.get("/equipment/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `equipment_register_${localToday()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Equipment register exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const notInvoiced = (o) => !o.invoiced && !o.branch_transfer;
  const ORDER_FILTERS = { all: () => true, not_invoiced: notInvoiced, transfers: (o) => !!o.branch_transfer };
  const shownOrders = orders.filter(ORDER_FILTERS[orderFilter]);
  const exportOrders = async () => {
    try {
      const res = await api.get("/hire/export", { params: { view: orderFilter }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `hire_orders_${orderFilter}_${localToday()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Hire orders exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const saveHire = async (e) => {
    e.preventDefault();
    setHireSaving(true);
    try {
      const payload = {
        equipment_id: hireForm.equipment_id,
        customer: hireForm.customer.trim(),
        quantity: Number(hireForm.quantity),
        start_date: hireForm.start_date,
        end_date: hireForm.end_date,
        rate_type: hireForm.rate_type,
        note: hireForm.note.trim(),
        customer_address: hireForm.customer_address.trim(),
        transport: hireForm.transport,
        transport_notes: hireForm.transport_notes.trim(),
        po_received: hireForm.po_received,
        po_number: hireForm.po_received ? hireForm.po_number.trim() : "",
        invoiced: hireForm.invoiced,
        branch_transfer: hireForm.branch_transfer,
        transfer_to: hireForm.branch_transfer ? hireForm.transfer_to : "",
      };
      if (editingHire) await api.put(`/hire-orders/${editingHire.id}`, payload);
      else await api.post("/hire-orders", payload);
      toast.success(editingHire ? "Hire order updated" : "Hire order created");
      setHireOpen(false);
      loadOrders();
      loadEquipment();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setHireSaving(false);
    }
  };

  const returnOrder = async (id) => {
    try {
      await api.patch(`/hire-orders/${id}/return`);
      toast.success("Marked as returned");
      loadOrders();
      loadEquipment();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const doDeleteHire = async () => {
    try {
      await api.delete(`/hire-orders/${hireDeleteId}`);
      toast.success("Hire order deleted");
      setHireDeleteId(null);
      loadOrders();
      loadEquipment();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setHireDeleteId(null);
    }
  };

  const rateOptions = selectedEquip
    ? [
        { v: "daily", label: `Daily — ${money(selectedEquip.daily_rate)}`, ok: selectedEquip.daily_rate != null },
        { v: "weekly", label: `Weekly — ${money(selectedEquip.weekly_rate)}`, ok: selectedEquip.weekly_rate != null },
        { v: "monthly", label: `Monthly — ${money(selectedEquip.monthly_rate)}`, ok: selectedEquip.monthly_rate != null },
      ].filter((o) => o.ok)
    : [{ v: "daily", label: "Daily", ok: true }];

  return (
    <div className="p-6 sm:p-8 max-w-[1600px] mx-auto" data-testid="equipment-page">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-heading text-4xl font-black tracking-tighter text-brand-display mt-1">Equipment Hire</h1>
        </div>
        {tab === "catalogue" && (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={exportRegister} data-testid="export-equipment-button"
              className="bg-zinc-100 text-zinc-900 border border-zinc-300 rounded-full font-bold tracking-tight hover:bg-zinc-200 transition-colors px-4 py-2.5 flex items-center gap-2 text-sm">
              <Download size={16} /> Export register
            </button>
            {canManageEquip && (<>
            <button onClick={() => setBulkEquipOpen(true)} data-testid="bulk-upload-equipment-button"
              className="bg-zinc-100 text-zinc-900 border border-zinc-300 rounded-full font-bold tracking-tight hover:bg-zinc-200 transition-colors px-4 py-2.5 flex items-center gap-2 text-sm">
              <Upload size={16} /> Upload CSV
            </button>
            <button onClick={openNewEquip} data-testid="add-equipment-button"
              className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2">
              <Plus size={18} /> Add Equipment
            </button>
            </>)}
          </div>
        )}
        {tab === "orders" && (
          <div className="flex items-center gap-2">
            <button onClick={() => setBulkHireOpen(true)} data-testid="bulk-upload-hire-button"
              className="bg-zinc-100 text-zinc-900 border border-zinc-300 rounded-full font-bold tracking-tight hover:bg-zinc-200 transition-colors px-4 py-2.5 flex items-center gap-2 text-sm">
              <Upload size={16} /> Upload CSV
            </button>
            <button onClick={openNewHire} data-testid="new-hire-button"
              className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-5 py-2.5 flex items-center gap-2">
              <Plus size={18} /> New Hire
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-zinc-200 mb-6">
        <button onClick={() => setTab("catalogue")} data-testid="tab-catalogue"
          className={`px-4 py-2.5 text-sm font-bold tracking-tight border-b-2 -mb-px flex items-center gap-2 ${tab === "catalogue" ? "border-brand text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}>
          <Package size={16} /> Equipment
        </button>
        <button onClick={() => setTab("orders")} data-testid="tab-orders"
          className={`px-4 py-2.5 text-sm font-bold tracking-tight border-b-2 -mb-px flex items-center gap-2 ${tab === "orders" ? "border-brand text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}>
          <ClipboardList size={16} /> Hire Orders
        </button>
      </div>

      {tab === "catalogue" && (
        <>
          {locations.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5" data-testid="equipment-location-filter">
              <button
                onClick={() => setLocFilter("all")}
                data-testid="loc-filter-all"
                className={`text-xs font-bold tracking-tight px-3 py-1.5 rounded-full border transition-colors ${locFilter === "all" ? "bg-brand text-white border-brand" : "bg-white text-zinc-600 border-zinc-300 hover:border-brand"}`}
              >
                All sites
              </button>
              {locations.map((l) => (
                <button
                  key={l.id}
                  onClick={() => setLocFilter(l.name)}
                  data-testid={`loc-filter-${l.name}`}
                  className={`text-xs font-bold tracking-tight px-3 py-1.5 rounded-full border transition-colors ${locFilter === l.name ? "bg-brand text-white border-brand" : "bg-white text-zinc-600 border-zinc-300 hover:border-brand"}`}
                >
                  {l.name}
                </button>
              ))}
            </div>
          )}
          {canManageEquip && <PendingRequestsPanel requests={requests} onDecided={() => { loadRequests(); loadEquipment(); }} />}
          <CatalogueFilters items={atSite} status={statusFilter} setStatus={setStatusFilter} due={dueFilter} setDue={setDueFilter} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="equipment-list">
            {catalogue.map((e) => (
            <div key={e.id} data-testid={`equipment-${e.id}`} className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <EquipmentThumb equipmentId={e.id} photoId={e.photos?.[0]?.id} className="w-16 h-16" testid={`equipment-thumb-${e.id}`} />
                <div className="min-w-0 break-words flex-1">
                  {e.asset_id && <div className="text-[11px] font-mono font-bold text-brand" data-testid={`equipment-asset-${e.id}`}>{e.asset_id}</div>}
                  <div className="font-heading text-lg font-bold tracking-tight text-zinc-900">{e.name}</div>
                  {requests.some((r) => r.equipment_id === e.id) && (
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mt-0.5" data-testid={`equipment-pending-${e.id}`}>Status change pending</div>
                  )}
                  {e.category && <div className="text-xs text-zinc-500 mt-0.5">{e.category}</div>}
                  {e.location && (
                    <div className="text-xs text-brand font-semibold mt-0.5 flex items-center gap-1" data-testid={`equipment-location-${e.id}`}>
                      <MapPin size={12} /> {e.location}
                    </div>
                  )}
                </div>
                {e.status === "available" && e.available <= 0 ? (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border shrink-0 bg-amber-100 text-amber-800 border-amber-300" data-testid={`equipment-badge-${e.id}`}>
                    Unavailable
                  </span>
                ) : (
                  <StatusBadge status={e.status} testid={`equipment-badge-${e.id}`} />
                )}
              </div>
              {e.description && <p className="text-sm text-zinc-600 mt-2 line-clamp-2">{e.description}</p>}
              {DUE_FIELDS.some((f) => e[f]) && (
                <div className="mt-2 flex flex-wrap gap-x-4 text-[11px]" data-testid={`equipment-due-${e.id}`}>
                  {DUE_FIELDS.filter((f) => e[f]).map((f) => (
                    <span key={f} className={isPastDue(e[f]) ? "text-red-600 font-bold" : "text-zinc-500"}>{DUE_SHORT[f]}: {fmtDate(e[f])}</span>
                  ))}
                </div>
              )}
              <div className="mt-3 grid grid-cols-3 gap-2 text-center border-y border-zinc-100 py-2">
                <div><div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Daily</div><div className="text-sm font-bold text-zinc-900">{money(e.daily_rate)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Weekly</div><div className="text-sm font-bold text-zinc-900">{money(e.weekly_rate)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Monthly</div><div className="text-sm font-bold text-zinc-900">{money(e.monthly_rate)}</div></div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs font-semibold text-zinc-600" data-testid={`equipment-stock-${e.id}`}>
                  <span className="text-emerald-700 font-bold">{e.available}</span> / {e.quantity} available
                </span>
                <div className="flex items-center gap-2">
                  <button onClick={() => setDetailId(e.id)} data-testid={`view-equipment-${e.id}`} className="text-xs font-bold text-zinc-600 hover:text-brand">Details</button>
                {(canManageEquip || canDeleteEquip) && (
                  <>
                    {canManageEquip && (
                      <button onClick={() => openEditEquip(e)} data-testid={`edit-equipment-${e.id}`} className="text-zinc-400 hover:text-brand"><Pencil size={16} /></button>
                    )}
                    {canDeleteEquip && (
                      <button onClick={() => setEqDeleteId(e.id)} data-testid={`delete-equipment-${e.id}`} className="text-zinc-400 hover:text-red-600"><Trash2 size={16} /></button>
                    )}
                  </>
                )}
                </div>
              </div>
            </div>
          ))}
          {catalogue.length === 0 && (
            <div className="col-span-full bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] px-5 py-10 text-center text-zinc-400 text-sm">
              {equipment.length === 0
                ? `No equipment yet.${canManageEquip ? " Add your first item." : ""}`
                : "No equipment matches these filters."}
            </div>
          )}
          </div>
        </>
      )}

      {tab === "orders" && (
        <>
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="hire-invoice-filter">
          {[["all", "All orders", "bg-zinc-900 border-zinc-900", "hover:border-zinc-900"],
            ["not_invoiced", "Not invoiced", "bg-amber-500 border-amber-500", "hover:border-amber-500"],
            ["transfers", "Transfers", "bg-sky-600 border-sky-600", "hover:border-sky-600"]].map(([key, label, on, hover]) => (
            <button key={key} onClick={() => setOrderFilter(key)} data-testid={`hire-filter-${key.replace("_", "-")}`}
              className={`text-xs font-bold tracking-tight px-3 py-1.5 rounded-full border transition-colors ${orderFilter === key ? `${on} text-white` : `bg-white text-zinc-600 border-zinc-300 ${hover}`}`}>
              {label} <span className="opacity-60 font-mono text-[10px]">{orders.filter(ORDER_FILTERS[key]).length}</span>
            </button>
          ))}
          <button onClick={exportOrders} data-testid="hire-orders-export-button"
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold tracking-tight px-3 py-1.5 rounded-full border border-zinc-300 bg-white text-zinc-700 hover:border-zinc-900 transition-colors">
            <Download size={14} /> Export CSV
          </button>
        </div>
        <div className="bg-white border border-zinc-200 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.08)] overflow-x-auto" data-testid="hire-orders-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 font-bold border-b border-zinc-200">
                <th className="px-4 py-3">Equipment</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Qty</th>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Rate</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Transport</th>
                <th className="px-4 py-3">PO</th>
                <th className="px-4 py-3">Invoiced</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {shownOrders.map((o) => (
                <tr key={o.id} data-testid={`hire-order-${o.id}`} className="hover:bg-zinc-50">
                  <td className="px-4 py-3 font-semibold text-zinc-900">{o.equipment_name}</td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">{o.location || "—"}</td>
                  <td className="px-4 py-3 text-zinc-700">
                    {o.customer}
                    {o.branch_transfer && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border bg-violet-100 text-violet-800 border-violet-300 whitespace-nowrap" data-testid={`hire-branch-transfer-${o.id}`}>
                        Branch transfer{o.transfer_to ? ` → ${o.transfer_to}` : ""}
                      </span>
                    )}
                    {o.customer_address && <div className="text-[11px] text-zinc-400 max-w-[200px] truncate" title={o.customer_address}>{o.customer_address}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{o.quantity}</td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">{o.start_date} → {o.end_date}</td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">{money(o.unit_price)} × {o.units} {o.unit_label}</td>
                  <td className="px-4 py-3 font-bold text-zinc-900">{money(o.total_cost)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-700" data-testid={`hire-transport-${o.id}`}>
                    {TRANSPORT[o.transport] || "—"}
                    {o.transport_notes && <div className="text-[11px] text-zinc-400 max-w-[180px] truncate" title={o.transport_notes}>{o.transport_notes}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setBillingOrder(o)} data-testid={`hire-po-${o.id}`}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border whitespace-nowrap hover:ring-2 hover:ring-brand ${o.po_received ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-zinc-100 text-zinc-500 border-zinc-300"}`}>
                      {o.po_received ? (o.po_number || "Yes") : "No"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {o.branch_transfer ? <span className="text-[10px] font-bold uppercase text-zinc-400" data-testid={`hire-invoiced-${o.id}`}>N/A</span> : (
                    <button onClick={() => setBillingOrder(o)} data-testid={`hire-invoiced-${o.id}`}
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border hover:ring-2 hover:ring-brand ${o.invoiced ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-amber-100 text-amber-800 border-amber-300"}`}>
                      {o.invoiced ? "Yes" : "No"}
                    </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${o.status === "active" ? "bg-blue-100 text-blue-800 border-blue-300" : "bg-zinc-200 text-zinc-600 border-zinc-400"}`}>
                      {o.status}
                    </span>
                    {o.overdue && (
                      <span data-testid={`overdue-badge-${o.id}`} className="ml-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border bg-red-100 text-red-700 border-red-300">
                        Overdue
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <a href={`/hire-orders/${o.id}/docket`} target="_blank" rel="noreferrer" data-testid={`docket-hire-${o.id}`}
                        className="text-xs font-bold text-zinc-600 hover:text-brand flex items-center gap-1" title="Print delivery docket">
                        <FileText size={14} /> Docket
                      </a>
                      {o.status === "active" && canEditHire && (
                        <button onClick={() => setExtendOrder(o)} data-testid={`extend-hire-${o.id}`}
                          className="text-xs font-bold text-blue-700 hover:text-blue-900 flex items-center gap-1">
                          <CalendarPlus size={14} /> Extend
                        </button>
                      )}
                      {o.status === "active" && canEditHire && (
                        <button onClick={() => openEditHire(o)} data-testid={`edit-hire-${o.id}`}
                          className="text-xs font-bold text-zinc-600 hover:text-brand flex items-center gap-1">
                          <Pencil size={14} /> Edit
                        </button>
                      )}
                      {o.status === "active" && (
                        <button onClick={() => returnOrder(o.id)} data-testid={`return-hire-${o.id}`}
                          className="text-xs font-bold text-emerald-700 hover:text-emerald-900 flex items-center gap-1">
                          <RotateCcw size={14} /> Return
                        </button>
                      )}
                      {canDeleteHire && (
                        <button onClick={() => setHireDeleteId(o.id)} data-testid={`delete-hire-${o.id}`} className="text-zinc-400 hover:text-red-600"><Trash2 size={16} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {shownOrders.length === 0 && (
                <tr><td colSpan={12} className="px-5 py-10 text-center text-zinc-400 text-sm">{orders.length === 0 ? "No hire orders yet." : orderFilter === "transfers" ? "No branch transfers yet." : "Every hire order has been invoiced."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      <EquipmentFormDialog open={eqOpen} onOpenChange={setEqOpen} editing={eqEditing} locations={locations} onSaved={loadEquipment} />
      <EquipmentDetailDialog equipment={detailEquip} onClose={() => setDetailId(null)} onEdit={openEditEquip}
        onChange={onDetailChange} canManage={canManageEquip} canLog={canLogHistory} canDelete={canDeleteEquip}
        canRequest={user.role === "hire_coordinator"} pendingRequest={requests.find((r) => r.equipment_id === detailId)} onRequested={loadRequests} />

      {/* Hire dialog */}
      <Dialog open={hireOpen} onOpenChange={setHireOpen}>
        <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tighter">{editingHire ? "Edit Hire Order" : "New Hire Order"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveHire} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Equipment</label>
              <select required value={hireForm.equipment_id}
                onChange={(e) => setHireForm({ ...hireForm, equipment_id: e.target.value, rate_type: "daily" })}
                data-testid="hire-equipment-select" className={inputCls}>
                <option value="">Select equipment…</option>
                {equipment.filter((e) => e.id === editingHire?.equipment_id || (!BLOCKING_STATUSES.includes(e.status) && e.available > 0)).map((e) => (
                  <option key={e.id} value={e.id}>{e.name}{e.location ? ` — ${e.location}` : ""} ({e.available + (e.id === editingHire?.equipment_id ? editingHire.quantity : 0)} available)</option>
                ))}
              </select>
              {selectedEquip && (
                <div className="flex items-center gap-3 mt-2" data-testid="hire-selected-equipment">
                  <EquipmentThumb equipmentId={selectedEquip.id} photoId={selectedEquip.photos?.[0]?.id} className="w-20 h-20" testid="hire-equipment-photo" />
                  <div className="text-xs text-zinc-500 min-w-0">
                    <div className="font-bold text-zinc-800">{selectedEquip.name}</div>
                    {[selectedEquip.make_model, selectedEquip.wll].filter(Boolean).join(" · ")}
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Customer</label>
                <input required value={hireForm.customer} onChange={(e) => setHireForm({ ...hireForm, customer: e.target.value })} data-testid="hire-customer-input" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Quantity</label>
                <input type="number" min="1" max={(selectedEquip?.available || 0) + (selectedEquip && selectedEquip.id === editingHire?.equipment_id ? editingHire.quantity : 0) || 1} required value={hireForm.quantity} onChange={(e) => setHireForm({ ...hireForm, quantity: e.target.value })} data-testid="hire-quantity-input" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Customer location / address</label>
              <textarea rows={2} maxLength={500} value={hireForm.customer_address} onChange={(e) => setHireForm({ ...hireForm, customer_address: e.target.value })} data-testid="hire-address-input" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Transport requirements</label>
                <select value={hireForm.transport} onChange={(e) => setHireForm({ ...hireForm, transport: e.target.value })} data-testid="hire-transport-select" className={inputCls}>
                  <option value="">Not specified</option>
                  {Object.entries(TRANSPORT).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Transport notes</label>
                <input maxLength={1000} value={hireForm.transport_notes} onChange={(e) => setHireForm({ ...hireForm, transport_notes: e.target.value })} data-testid="hire-transport-notes-input" className={inputCls} placeholder="e.g. Hiab truck, site access" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">PO received</label>
                <YesNo value={hireForm.po_received} onChange={(v) => setHireForm({ ...hireForm, po_received: v })} testid="hire-po" />
                {hireForm.po_received && (
                  <input maxLength={100} value={hireForm.po_number} onChange={(e) => setHireForm({ ...hireForm, po_number: e.target.value })} data-testid="hire-po-number-input" className={`${inputCls} mt-2`} placeholder="PO number" />
                )}
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Invoiced</label>
                <YesNo value={hireForm.invoiced} onChange={(v) => setHireForm({ ...hireForm, invoiced: v })} testid="hire-invoiced" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">Start date</label>
                <input type="date" required value={hireForm.start_date} onChange={(e) => setHireForm({ ...hireForm, start_date: e.target.value })} data-testid="hire-start-input" className={inputCls} />
              </div>
              <div>
                <label className="text-sm font-bold text-zinc-900 block mb-1">End date</label>
                <input type="date" required value={hireForm.end_date} onChange={(e) => setHireForm({ ...hireForm, end_date: e.target.value })} data-testid="hire-end-input" className={inputCls} />
              </div>
            </div>
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Rate</label>
              <select value={hireForm.rate_type} onChange={(e) => setHireForm({ ...hireForm, rate_type: e.target.value })} data-testid="hire-rate-select" className={inputCls}>
                {rateOptions.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
            </div>
            <BranchTransferField form={hireForm} setForm={setHireForm} locations={locations} fromLocation={selectedEquip?.location} />
            <div>
              <label className="text-sm font-bold text-zinc-900 block mb-1">Note (optional)</label>
              <input value={hireForm.note} onChange={(e) => setHireForm({ ...hireForm, note: e.target.value })} data-testid="hire-note-input" className={inputCls} />
            </div>
            {editingHire && <HireChangeHistory history={editingHire.history} />}
            <DialogFooter>
              <button type="submit" disabled={hireSaving} data-testid="hire-submit-button"
                className="bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2 disabled:opacity-60">
                {hireSaving ? "Saving…" : editingHire ? "Save changes" : "Create hire order"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!eqDeleteId} onOpenChange={(o) => !o && setEqDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this equipment?</AlertDialogTitle>
            <AlertDialogDescription>Equipment with active hire orders cannot be deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteEquip} data-testid="confirm-delete-equipment" className="bg-red-600 hover:bg-red-700 rounded-none">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!hireDeleteId} onOpenChange={(o) => !o && setHireDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this hire order?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the booking record.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteHire} data-testid="confirm-delete-hire" className="bg-red-600 hover:bg-red-700 rounded-none">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <HireBillingDialog order={billingOrder} onClose={() => setBillingOrder(null)} onSaved={loadOrders} />
      <ExtendHireDialog order={extendOrder} onClose={() => setExtendOrder(null)} onSaved={loadOrders} />
      <BulkUpload
        open={bulkEquipOpen}
        onOpenChange={setBulkEquipOpen}
        endpoint="/equipment/bulk"
        templateName="equipment_template.csv"
        templateContent={EQUIPMENT_CSV_TEMPLATE}
        onDone={loadEquipment}
        testidPrefix="equipment-bulk"
      />
      <BulkUpload
        open={bulkHireOpen}
        onOpenChange={setBulkHireOpen}
        endpoint="/hire-orders/bulk"
        templateName="hire_orders_template.csv"
        templateContent={HIRE_CSV_TEMPLATE}
        onDone={() => { loadOrders(); loadEquipment(); }}
        testidPrefix="hire-bulk"
      />
    </div>
  );
}
